// @ts-check
import classNames from 'classnames';
import React, { useState } from 'react';
import Link from './Link';

import './AccordionMenu.scss';

/**
 * @typedef AccordionItemProps
 * @property {number} depth The depth this accordion is at.
 * @property {boolean} isOpen A value indicating whether this Accordion Item is expanded.
 * @property {import('~/components/Core/SiteLinks').LinkItem} item The item that is rendered on this item.
 * @property {Function} onOpen A function to execute when the toggle is requested.
 */
/** @type {import('~/types/React').ReactFC<AccordionItemProps>} */
const AccordionItem = ({ className, depth, onOpen, isOpen, item, ...rest }) => {
	const hasChildren = item.children?.length > 0;

	let itemLink = !item.link ? (
		<span className="menu-label-text" onClick={() => onOpen()}>
			{!!item.leftComponent && React.cloneElement(item.leftComponent, { className: 'menu-item-left-comp' })}
			<span>{item.name}</span>
		</span>
	) : (
		<Link to={item.link} className="menu-label-text" tabIndex={0}>
			{!!item.leftComponent && React.cloneElement(item.leftComponent, { className: 'menu-item-left-comp' })}
			<span>{item.name}</span>
		</Link>
	);

	// If we have children, wrap the link with a sub-menu.
	if (hasChildren) {
		itemLink = (
			<React.Fragment>
				<div className="menu-label">
					{itemLink}

					<button
						className="expandable-section"
						aria-label="Toggle Section"
						title="Toggle Section"
						onClick={() => onOpen()}
					>
						<span className="right-arrow">arrow_forward_ios</span>
					</button>
				</div>
				<AccordionList items={item.children} depth={depth + 1} aria-hidden={!isOpen} />
			</React.Fragment>
		);
	}

	return (
		<li
			{...rest}
			className={classNames('menu-item', className, {
				'with-children': hasChildren,
				'is-open': hasChildren && isOpen,
				'is-closed': hasChildren && !isOpen,
			})}
		>
			{itemLink}
		</li>
	);
};

/**
 * @typedef AccordionListProps
 * @property {number} depth The depth this accordion is at.
 * @property {number} [initialIndexOpen=-1] The optional index of the first open accordion child item.0
 * @property {import('~/components/Core/SiteLinks').LinkItem[]} items The items within this Accordion List.
  */
/** @type {import('~/types/React').ReactFC<AccordionListProps>} */
const AccordionList = ({ className, depth, initialIndexOpen = -1, items, ...rest }) => {
	const [openedIndex, setOpenedIndex] = useState(initialIndexOpen);
	return (
		<ul {...rest} className={classNames('menu-list', `depth-${depth}`, className)}>
			{items.map((item, i) => (
				<AccordionItem
					item={item}
					depth={depth}
					isOpen={i === openedIndex}
					onOpen={() => setOpenedIndex(openedIndex === i ? -1 : i)}
					key={i}
				/>
			))}
		</ul>
	);
};

/**
 * @typedef AccordionMenuProps
 * @property {import('~/components/Core/SiteLinks').LinkItem[]} items
 * @property {number} [initialIndexOpen=-1]
  */
/** @type {import('~/types/React').ReactFC<AccordionMenuProps>} */
const AccordionMenu = ({ items, className, initialIndexOpen = -1, ...rest }) => (
	<aside className={classNames('accordion-menu', className)} {...rest}>
		<AccordionList items={items} depth={0} initialIndexOpen={initialIndexOpen} />
	</aside>
);

export default AccordionMenu;
